import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { authApi } from '@/lib/api'
import type { Campaign } from '@/lib/referral-types'
import { Card, CardContent, CardHeader } from '@/components/ui/Card'
import { UserSearchInput } from '@/components/ui/UserSearchInput'

export function ManualAttributionPage() {
  const queryClient = useQueryClient()

  const [referrerUserId, setReferrerUserId] = useState('')
  const [referredUserId, setReferredUserId] = useState('')
  const [campaignId, setCampaignId] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null)

  const { data: campaignsData } = useQuery({
    queryKey: ['admin-referral-campaigns'],
    queryFn: () => authApi.get<{ campaigns: Campaign[] }>('/api/v1/admin/referral/campaigns'),
  })

  async function handleAttribute() {
    if (!referrerUserId.trim() || !referredUserId.trim()) return
    setSubmitting(true)
    setResult(null)
    try {
      const res = await authApi.post<{ referral_id: string }>('/api/v1/admin/referral/attribute', {
        referrer_user_id: referrerUserId.trim(),
        referred_user_id: referredUserId.trim(),
        campaign_id: campaignId || undefined,
      })
      setResult({ success: true, message: `Attribution created (ID: ${res.referral_id}).` })
      queryClient.invalidateQueries({ queryKey: ['admin-referrals'] })
      queryClient.invalidateQueries({ queryKey: ['admin-referral-overview'] })
      setReferrerUserId('')
      setReferredUserId('')
      setCampaignId('')
    } catch (e) {
      setResult({ success: false, message: String(e) })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div>
      <h1 className="text-2xl font-heading font-semibold mb-6">Manual Attribution</h1>

      <Card className="max-w-lg">
        <CardHeader>
          <h3 className="font-heading font-semibold text-sm">Link Referrer to Referred User</h3>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-[var(--content-tertiary)]">
            Manually attribute a referred user to a referrer. This creates a referral record with attribution_type = 'manual' and fires a trial_signup event. The referred user must not already have an existing referral attribution.
          </p>

          <UserSearchInput
            label="Referrer"
            value={referrerUserId}
            onChange={(id) => setReferrerUserId(id)}
            placeholder="Search referrer by name or email..."
          />

          <UserSearchInput
            label="Referred User"
            value={referredUserId}
            onChange={(id) => setReferredUserId(id)}
            placeholder="Search referred user by name or email..."
          />

          <div>
            <label className="block text-xs text-[var(--content-tertiary)] mb-1">Campaign (optional)</label>
            <select
              value={campaignId}
              onChange={(e) => setCampaignId(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-[var(--surface-nested)] border border-[var(--border)] rounded-lg text-[var(--content)] outline-none cursor-pointer"
            >
              <option value="">No campaign (default rules)</option>
              {campaignsData?.campaigns?.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          {result && (
            <p className={`text-xs ${result.success ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {result.message}
            </p>
          )}

          <button
            onClick={handleAttribute}
            disabled={submitting || !referrerUserId.trim() || !referredUserId.trim()}
            className="w-full px-4 py-2 text-sm rounded-full bg-[#65A0A8] text-white hover:bg-[#5a9098] disabled:opacity-50 transition-colors cursor-pointer"
          >
            {submitting ? 'Creating...' : 'Create Attribution'}
          </button>
        </CardContent>
      </Card>
    </div>
  )
}
